import { Link } from 'react-router-dom'
import { Container } from '@/components/layout/Container'
import { LinkButton } from '@/components/ui/LinkButton'
import { useCartStore } from '@/stores/cartStore'
import { formatPrice } from '@/lib/utils'
import { Minus, Plus, X, Gift, Truck } from 'lucide-react'

export function Cart() {
  const {
    items,
    updateQuantity,
    removeItem,
    giftWrap,
    toggleGiftWrap,
    subtotal,
    total,
  } = useCartStore()

  return (
    <div className="pt-28 pb-24">
      <Container className="max-w-4xl">
        <h1 className="mb-8 font-display text-4xl normal-case text-carbon">your bag</h1>

        {items.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-carbon">Your bag is empty.</p>
            <Link to="/shop" className="mt-4 inline-block text-carbon hover:underline">
              Continue shopping
            </Link>
          </div>
        ) : (
          <div className="grid gap-12 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              {items.map((item) => (
                <div key={item.id} className="flex gap-4 border-b border-ink/10 pb-6">
                  <img
                    src={item.image}
                    alt={item.perfumeName}
                    className="h-28 w-24 object-cover"
                  />
                  <div className="flex flex-1 flex-col justify-between">
                    <div>
                      <Link
                        to={`/product/${item.perfumeId}`}
                        className="font-display text-lg normal-case text-carbon hover:text-wd-orange"
                      >
                        {item.perfumeName.toLowerCase()}
                      </Link>
                      <p className="text-sm text-carbon/60">{item.sizeLabel}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center border border-ink/10">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="px-3 py-1 text-carbon hover:text-wd-orange"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="px-3 text-sm">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="px-3 py-1 text-carbon hover:text-wd-orange"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <span className="text-carbon">{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="self-start text-carbon/30 hover:text-wd-orange"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="bg-wd-mist p-6">
              <div className="mb-4 flex items-start gap-2 text-xs text-carbon/60">
                <Truck className="h-4 w-4 shrink-0" />
                <span>Free shipping on orders over ₦30,000.</span>
              </div>

              <button
                onClick={toggleGiftWrap}
                className="mb-6 flex w-full items-center justify-between border border-ink/10 p-4 text-left"
              >
                <div className="flex items-center gap-3">
                  <Gift className="h-4 w-4 text-wd-orange" />
                  <div>
                    <p className="text-sm text-carbon">Add gift wrap</p>
                    <p className="text-xs text-carbon/60">+{formatPrice(2500)} per order</p>
                  </div>
                </div>
                <div
                  className={`h-4 w-4 rounded-full border ${
                    giftWrap ? 'border-wd-orange bg-wd-orange' : 'border-ink/20'
                  }`}
                />
              </button>

              <div className="mb-2 flex justify-between text-sm text-carbon/60">
                <span>Subtotal</span>
                <span>{formatPrice(subtotal())}</span>
              </div>
              {giftWrap && (
                <div className="mb-2 flex justify-between text-sm text-carbon/60">
                  <span>Gift wrap</span>
                  <span>{formatPrice(2500)}</span>
                </div>
              )}
              <div className="mb-6 flex justify-between text-lg text-carbon">
                <span className="font-display">Total</span>
                <span className="font-display">{formatPrice(total())}</span>
              </div>

              <LinkButton to="/checkout" className="w-full">
                Checkout
              </LinkButton>
            </div>
          </div>
        )}
      </Container>
    </div>
  )
}
